import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, addDoc, deleteDoc, serverTimestamp, orderBy, updateDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import ProgressiveImage from '../components/ProgressiveImage';
import './DesignDetailPage.css';

const sanitizeComment = (text) => {
  const element = document.createElement('div');
  element.innerText = text;
  return element.innerHTML.replace(/<br>/g, '\n');
};

const DesignDetailPage = () => {
  const { designId } = useParams();
  const { currentUser } = useAuth();
  const [design, setDesign] = useState(null);
  const [designer, setDesigner] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState('');
  const commentTextareaRef = useRef(null);

  useEffect(() => {
    const fetchDesignAndComments = async () => {
      try {
        setLoading(true);
        // Fetch design data
        const designDocRef = doc(db, 'designs', designId);
        const designDocSnap = await getDoc(designDocRef);

        if (designDocSnap.exists()) {
          const designData = { id: designDocSnap.id, ...designDocSnap.data() };
          setDesign(designData);

          // Fetch designer data
          const userDocRef = doc(db, 'users', designData.userId);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            setDesigner(userDocSnap.data());
          }

          // Fetch comments
          const commentsQuery = query(
            collection(db, 'comments'),
            where('designId', '==', designId),
            orderBy('createdAt', 'desc')
          );
          const commentsSnapshot = await getDocs(commentsQuery);
          const commentsData = commentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setComments(commentsData);

        } else {
          setError('Design not found.');
        }
      } catch (err) {
        setError('Failed to fetch design data.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDesignAndComments();
  }, [designId]);

  // Auto-resize textarea when content changes
  useEffect(() => {
    if (commentTextareaRef.current) {
      commentTextareaRef.current.style.height = 'auto';
      commentTextareaRef.current.style.height = commentTextareaRef.current.scrollHeight + 'px';
    }
  }, [newComment]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    const sanitizedText = sanitizeComment(newComment);

    try {
      const commentData = {
        designId: designId,
        userId: currentUser.uid,
        userName: currentUser.displayName || 'Anonymous',
        userAvatar: currentUser.photoURL,
        text: sanitizedText,
        createdAt: serverTimestamp(),
      };
      const docRef = await addDoc(collection(db, 'comments'), commentData);
      setComments([{ id: docRef.id, ...commentData, createdAt: { toDate: () => new Date() } }, ...comments]);
      setNewComment('');
    } catch (error) {
      console.error("Error adding comment: ", error);
      setError("Failed to post comment.");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm("Are you sure you want to delete this comment?")) return;

    try {
      await deleteDoc(doc(db, 'comments', commentId));
      setComments(comments.filter(comment => comment.id !== commentId));
    } catch (error) {
      console.error("Error deleting comment: ", error);
      setError("Failed to delete comment.");
    }
  };

  const handleStartEditingName = () => {
    setIsEditingName(true);
    setEditingName(design.name || '');
  };

  const handleCancelEditingName = () => {
    setIsEditingName(false);
    setEditingName('');
  };

  const handleSaveName = async () => {
    if (!editingName.trim()) {
      setError("Design name cannot be empty.");
      return;
    }

    try {
      const designRef = doc(db, 'designs', designId);
      await updateDoc(designRef, {
        name: editingName.trim()
      });
      
      setDesign(prev => ({ ...prev, name: editingName.trim() }));
      setIsEditingName(false);
      setEditingName('');
    } catch (error) {
      console.error("Error updating design name: ", error);
      setError("Failed to update design name.");
    }
  };

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  if (error) {
    return <div className="error-container">{error}</div>;
  }

  if (!design) {
    return null;
  }

  return (
    <div className="design-detail-page">
      <div className="page-header">
        {isEditingName ? (
          <div className="name-editing-container">
            <input
              type="text"
              value={editingName}
              onChange={(e) => setEditingName(e.target.value)}
              placeholder="Enter design name..."
              className="name-input"
              autoFocus
            />
            <div className="name-edit-buttons">
              <button onClick={handleSaveName} className="save-name-btn">
                Save
              </button>
              <button onClick={handleCancelEditingName} className="cancel-name-btn">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="name-display-container">
            {currentUser?.uid === design.userId && (!design.name || design.name === 'No Name') && (
              <button onClick={handleStartEditingName} className="edit-name-btn">
                Edit Name
              </button>
            )}
            <h1>{design.name || 'No Name'}</h1>
          </div>
        )}
        <p>
          Created by{' '}
          <Link to={`/gallery/user/${design.userId}`} className="designer-link">
            {designer?.displayName || 'Unknown Designer'}
          </Link>
        </p>
      </div>

      <div className="design-detail-content">
        <div className="environment-section">
          <h2>Environment Photo</h2>
          <ProgressiveImage 
            src={design.environmentPhotoUrl || design.sourceImageUrl} 
            alt="Environment source" 
            className="detail-image"
          />
          {design.environmentDescription && (
            <div className="environment-description">
              <h3>Environment Description</h3>
              <p>{design.environmentDescription}</p>
            </div>
          )}
        </div>
        
        <div className="design-variants-section">
          <h2>Generated Camo Designs</h2>
          <div className="design-variants">
            {/* Design A - Always on the left */}
            <div className={`design-variant ${design.selectedVariant === 'A' ? 'selected' : ''}`}>
              <h3>
                Design A
                {design.selectedVariant === 'A' && (
                  <span className="selected-badge">✓ Selected</span>
                )}
              </h3>
              <ProgressiveImage 
                src={design.selectedVariant === 'A' && design.hasEdits && design.finalDesignChoice === 'edited' ? design.editedImageUrl : design.designAUrl} 
                alt="Camo design A" 
                className="detail-image"
              />
              {design.promptA && (
                <div className="prompt-info">
                  <h4>Generation Prompt</h4>
                  <p>{design.promptA}</p>
                </div>
              )}
            </div>
            
            {/* Design B - Always on the right */}
            <div className={`design-variant ${design.selectedVariant === 'B' ? 'selected' : ''}`}>
              <h3>
                Design B
                {design.selectedVariant === 'B' && (
                  <span className="selected-badge">✓ Selected</span>
                )}
              </h3>
              <ProgressiveImage 
                src={design.selectedVariant === 'B' && design.hasEdits && design.finalDesignChoice === 'edited' ? design.editedImageUrl : design.designBUrl} 
                alt="Camo design B" 
                className="detail-image"
              />
              {design.promptB && (
                <div className="prompt-info">
                  <h4>Generation Prompt</h4>
                  <p>{design.promptB}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="design-metadata">
        <h2>Details</h2>
        <ul>
          <li><strong>Date Created:</strong> {design.createdAt?.toDate().toLocaleDateString()}</li>
          <li><strong>Location:</strong> {design.county && design.county !== 'N/A' ? `${design.county}, ${design.state}` : design.state}</li>
          <li><strong>Selected Variant:</strong> Design {design.selectedVariant || 'A'}</li>
          <li><strong>Votes:</strong> {design.voteCount || 0} ❤️</li>
        </ul>
        {design.story && (
          <div className="design-story">
            <h3>Designer's Story</h3>
            <p>{design.story}</p>
          </div>
        )}
      </div>

      <div className="comments-section">
        <h2>Comments ({comments.length})</h2>
        <form onSubmit={handleCommentSubmit} className="comment-form">
          <textarea
            ref={commentTextareaRef}
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Leave a comment..."
            rows="1"
            style={{ resize: 'none', overflow: 'hidden' }}
          />
          <button type="submit">Post Comment</button>
        </form>
        <div className="comment-list">
          {comments.map(comment => (
            <div key={comment.id} className="comment-item">
              <div className="comment-header">
                <span className="comment-author">{comment.userName}</span>
                <span className="comment-date">
                  {comment.createdAt?.toDate().toLocaleDateString()}
                </span>
                {(currentUser?.uid === design.userId || currentUser?.uid === comment.userId) && (
                  <button 
                    onClick={() => handleDeleteComment(comment.id)} 
                    className="delete-comment-btn"
                  >
                    Delete
                  </button>
                )}
              </div>
              <p className="comment-text">{comment.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DesignDetailPage; 